'use client'

import { useState, useEffect, useRef } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed'
    platform: string
  }>
  prompt(): Promise<void>
}

// 全局標記：確保事件監聽器只綁定一次（避免多個組件實例重複綁定）
let globalPromptHandler: ((e: Event) => void) | null = null
let globalInstallHandler: (() => void) | null = null
let hasInitialized = false

/**
 * ⚠️ PWA 安裝提示組件已禁用（2025/01/02）
 * 
 * 禁用原因：與 PWARegister 一起禁用，避免用戶看到安裝提示
 * 
 * 如需重新啟用，請同時啟用 PWARegister 組件
 */
export default function PWAInstallPrompt() {
  // ✅ 完全禁用 PWA 安裝提示
  return null

  // 以下代碼已禁用，保留作為參考
  /*
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showInstallPrompt, setShowInstallPrompt] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isMobileDevice, setIsMobileDevice] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // 檢測是否為移動設備（手機/平板）
  const checkIsMobileDevice = () => {
    // 明確排除桌面瀏覽器
    const userAgent = navigator.userAgent.toLowerCase();
    
    // 明確排除桌面作業系統
    const isDesktopOS = userAgent.includes('windows') || 
                       userAgent.includes('macintosh') || 
                       (userAgent.includes('linux') && !userAgent.includes('android'));
    
    // 方法 1: 檢測 user agent 中的移動設備標識
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    const isMobileUserAgent = mobileRegex.test(userAgent);
    
    // 方法 2: 檢測觸控設備
    const hasTouchScreen = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // 方法 3: 檢測 pointer 類型（觸控優先）
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    
    // 如果明確是桌面作業系統，且不是觸控設備，直接返回 false
    if (isDesktopOS) {
      // 桌面觸控設備（如 Surface）可能也有觸控，但我們主要針對手機/平板
      // 如果有觸控但屏幕很大，可能是桌面平板，也需要判斷
      if (!hasTouchScreen || window.innerWidth >= 1024) {
        return false;
      }
      // 桌面觸控設備但有觸控指標，可能是平板模式，允許顯示
      if (hasTouchScreen && hasCoarsePointer && window.innerWidth < 1024) {
        return true;
      }
    }
    
    // 最終判斷：必須是移動設備 user agent 或明確的觸控設備，且不是桌面瀏覽器
    // 對於平板，即使屏幕較大也應該顯示（如果是觸控設備）
    return (isMobileUserAgent || (hasTouchScreen && hasCoarsePointer)) && !isDesktopOS;
  }

  useEffect(() => {
    // 檢查是否為移動設備
    const mobileCheck = checkIsMobileDevice()
    setIsMobileDevice(mobileCheck)
    
    // 如果不是移動設備，直接返回，不顯示安裝提示
    if (!mobileCheck) {
      return
    }
    
    // 檢查是否已經安裝（加強版：多種檢查方式）
    const checkIfInstalled = () => {
      // 方法 1: 檢查是否在獨立模式下運行（Android/Chrome）
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true)
        return true
      }
      
      // 方法 2: 檢查是否在 iOS 上且已添加到主螢幕
      if ((window.navigator as any).standalone === true) {
        setIsInstalled(true)
        return true
      }
      
      // 方法 3: 檢查是否在 PWA 模式下（window.navigator.standalone 或 display-mode）
      if (window.matchMedia('(display-mode: standalone)').matches || 
          (window.navigator as any).standalone) {
        setIsInstalled(true)
        return true
      }
      
      return false
    }

    // 初始檢查
    checkIfInstalled()
    
    // 定期檢查（防止動態變化，例如用戶在當前會話中安裝了 PWA）
    const checkInterval = setInterval(() => {
      if (checkIfInstalled()) {
        clearInterval(checkInterval)
      }
    }, 2000) // 每 2 秒檢查一次
    
    // 監聽 display-mode 變化
    const mediaQuery = window.matchMedia('(display-mode: standalone)')
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true)
        clearInterval(checkInterval)
      }
    }
    mediaQuery.addEventListener('change', handleDisplayModeChange)

    // 只在第一次初始化時設置全局處理器
    if (!hasInitialized) {
      // 監聽 beforeinstallprompt 事件
      const handleBeforeInstallPrompt = (e: Event) => {
        // 只在確實要顯示自定義提示時才阻止預設行為
        // 檢查是否在 24 小時內被拒絕過
        const dismissedTime = localStorage.getItem('pwa-install-dismissed')
        const wasDismissed = dismissedTime && Date.now() - parseInt(dismissedTime) < 24 * 60 * 60 * 1000
        
        // 如果已經拒絕過，不要阻止預設行為，讓瀏覽器自己處理
        if (!wasDismissed) {
          e.preventDefault()
          const promptEvent = e as BeforeInstallPromptEvent
          
          // 設置 deferredPrompt（所有實例共享）
          if (typeof window !== 'undefined') {
            // 使用全局事件分發給所有實例
            window.dispatchEvent(new CustomEvent('pwa-prompt-ready', { 
              detail: promptEvent 
            }))
          }
        }
      }

      // 監聽 appinstalled 事件
      const handleAppInstalled = () => {
        // 通知所有實例
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('pwa-installed'))
        }
      }

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.addEventListener('appinstalled', handleAppInstalled)
      
      globalPromptHandler = handleBeforeInstallPrompt
      globalInstallHandler = handleAppInstalled
      hasInitialized = true
    }

    // 監聽全局事件（用於組件間通信）
    const handlePromptReady = (e: CustomEvent) => {
      const promptEvent = e.detail as BeforeInstallPromptEvent
      setDeferredPrompt(promptEvent)
      
      // 清除之前的延遲
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      
      // 延遲顯示安裝提示，避免過於突兀
      timeoutRef.current = setTimeout(() => {
        setShowInstallPrompt(true)
      }, 3000)
    }

    const handleAppInstalledGlobal = () => {
      setIsInstalled(true)
      setShowInstallPrompt(false)
      setDeferredPrompt(null)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      
      // 顯示安裝成功提示
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(() => {
          // console.log('🎉 PWA 安裝成功！')
        })
      }
    }

    window.addEventListener('pwa-prompt-ready', handlePromptReady as EventListener)
    window.addEventListener('pwa-installed', handleAppInstalledGlobal)

    return () => {
      // 清理全局事件監聽器
      window.removeEventListener('pwa-prompt-ready', handlePromptReady as EventListener)
      window.removeEventListener('pwa-installed', handleAppInstalledGlobal)
      
      // 清理定期檢查
      clearInterval(checkInterval)
      mediaQuery.removeEventListener('change', handleDisplayModeChange)
      
      // 清理延遲
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      
      // 如果是最後一個實例卸載，清理全局監聽器
      // 注意：這在 Next.js 中很難準確判斷，所以保留全局監聽器是安全的
    }
  }, [])

  const handleInstallClick = async () => {
    if (!deferredPrompt) return

    try {
      await deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        // console.log('✅ 用戶接受了安裝提示')
      } else {
        // console.log('❌ 用戶拒絕了安裝提示')
      }
      
      setDeferredPrompt(null)
      setShowInstallPrompt(false)
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('安裝提示失敗:', error)
      }
    }
  }

  const handleDismiss = () => {
    setShowInstallPrompt(false)
    // 立即保存拒絕時間，24小時後再次顯示
    localStorage.setItem('pwa-install-dismissed', Date.now().toString())
    setDeferredPrompt(null) // 清除 deferredPrompt，避免重複顯示
  }

  // 如果不是移動設備，不顯示
  if (!isMobileDevice) {
    return null
  }

  // 如果已經安裝，不顯示
  if (isInstalled) {
    return null
  }

  // 檢查是否在24小時內被拒絕過（在顯示前再次檢查，確保準確性）
  const dismissedTime = localStorage.getItem('pwa-install-dismissed')
  const wasDismissed = dismissedTime && Date.now() - parseInt(dismissedTime) < 24 * 60 * 60 * 1000
  
  if (wasDismissed) {
    return null
  }

  // 如果沒有安裝提示或沒有 deferredPrompt，不顯示
  if (!showInstallPrompt || !deferredPrompt) {
    return null
  }

  // 檢測是否為 iOS 設備
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm">
      <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <span className="text-white text-sm font-bold">JLPT</span>
              </div>
              <h3 className="text-white font-semibold">
                {isIOS ? '添加到主畫面' : '安裝 JLPT AI Tutor'}
              </h3>
            </div>
            <p className="text-gray-300 text-sm mb-2">
              {isIOS 
                ? '添加到主畫面，享受完整學習體驗'
                : '安裝到手機，享受完整學習體驗'
              }
            </p>
            <div className="text-gray-300 text-xs mb-3 space-y-1">
              <div>✓ 離線學習（常用內容可離線，完整內容需訪問後緩存）</div>
              <div>✓ 更快載入速度</div>
              <div>✓ 桌面快捷圖標</div>
              <div>✓ 全螢幕體驗</div>
            </div>
            <p className="text-gray-400 text-xs mb-3">
              * 僅提供付費用戶，且僅支援手機/平板設備
            </p>
            {isIOS && (
              <div className="text-gray-400 text-xs mb-3 p-2 bg-gray-900/50 rounded">
                <p className="font-semibold mb-1">iOS 安裝步驟：</p>
                <p>1. 點擊瀏覽器底部的「分享」按鈕</p>
                <p>2. 選擇「加入主畫面」</p>
                <p>3. 點擊「加入」完成</p>
              </div>
            )}
            <div className="flex space-x-2">
              {!isIOS && (
                <button
                  onClick={handleInstallClick}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  立即安裝
                </button>
              )}
              <button
                onClick={handleDismiss}
                className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                稍後提醒
              </button>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-gray-400 hover:text-white ml-2"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  )
  */
}
